<template>
  <HrPageLayout
    title="Holiday Categories"
    subtitle="Organize holidays by category"
    icon="🏷️"
    :breadcrumb-items="[{ label: 'Company Settings' }, { label: 'Holidays', route: '/holidays' }, { label: 'Categories' }]"
  >
    <template #header-actions>
      <button @click="createCategory" class="hr-btn hr-btn-primary">
        <i class="mdi mdi-plus"></i> Add Category
      </button>
    </template>

    <div class="container-fluid">
      <!-- Top cards and action buttons -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <h5 class="card-title mb-3">Manage Holiday Categories</h5>
                    <p class="text-muted">
                      Create and manage categories to organize your company holidays. Categories help employees quickly
                      identify the type of holiday and can be used for reporting and filtering.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Categories List -->
            <div class="row">
              <div class="col-lg-8">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title">Categories List</h5>
                  </div>
                  <div class="card-body">
                    <!-- Loading state -->
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading categories...</p>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="!categories.length" class="text-center p-5">
                      <i class="mdi mdi-tag-outline font-size-48 text-muted"></i>
                      <h5 class="mt-2">No categories found</h5>
                      <p class="text-muted">Click the "Add Category" button to create your first category</p>
                    </div>

                    <!-- Categories table -->
                    <div v-else class="table-responsive">
                      <table class="table table-hover align-middle">
                        <thead class="table-light">
                        <tr>
                          <th>Category</th>
                          <th>Description</th>
                          <th>Usage</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="category in categories" :key="category.id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="category-color-dot me-2" :style="{ backgroundColor: category.color_code }"></div>
                              <span class="fw-medium">{{ category.category_name }}</span>
                            </div>
                          </td>
                          <td>
                            <p class="text-muted mb-0 small">{{ truncateText(category.description, 80) || 'No description' }}</p>
                          </td>
                          <td>
                            <span class="badge bg-light text-dark">{{ getCategoryUsage(category.id) }} holidays</span>
                          </td>
                          <td class="text-center">
                            <button @click="editCategory(category)" class="btn btn-sm btn-primary me-1" title="Edit">
                              <i class="mdi mdi-pencil"></i>
                            </button>
                            <button
                                @click="deleteCategory(category)"
                                class="btn btn-sm btn-danger"
                                title="Delete"
                                :disabled="isCategoryInUse(category.id)"
                            >
                              <i class="mdi mdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Holiday Distribution By Category -->
              <div class="col-lg-4">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title">Holiday Distribution</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="loading" class="text-center py-5">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                    <div v-else-if="categoryUsage.length === 0" class="text-center py-5">
                      <i class="mdi mdi-chart-pie font-size-48 text-muted"></i>
                      <p class="mt-2">No holiday data available</p>
                    </div>
                    <div v-else>
                      <div class="category-distribution mb-4">
                        <div v-for="category in categoryUsage" :key="category.id" class="category-bar mb-3">
                          <div class="d-flex justify-content-between mb-1">
                            <span class="small">{{ category.name }}</span>
                            <span class="small text-muted">{{ category.count }} holiday{{ category.count !== 1 ? 's' : '' }}</span>
                          </div>
                          <div class="progress" style="height: 10px;">
                            <div class="progress-bar" :style="{ width: `${category.percentage}%`, backgroundColor: category.color }"></div>
                          </div>
                        </div>
                      </div>
                      <div class="text-center text-muted small">
                        Based on holidays in the current year
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
    </div>

  <!-- Category Create/Edit Modal -->
  <div class="modal fade" id="categoryModal" tabindex="-1" aria-labelledby="categoryModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="categoryModalLabel">{{ isEditMode ? 'Edit Category' : 'Add New Category' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form id="categoryForm" @submit.prevent="submitCategory">
            <div class="mb-3">
              <label for="categoryName" class="form-label">Category Name <span class="text-danger">*</span></label>
              <input
                  type="text"
                  class="form-control"
                  id="categoryName"
                  v-model="categoryForm.categoryName"
                  required
                  placeholder="E.g., Public Holiday, Religious Holiday"
              >
            </div>
            <div class="mb-3">
              <label for="categoryDescription" class="form-label">Description</label>
              <textarea
                  class="form-control"
                  id="categoryDescription"
                  v-model="categoryForm.description"
                  rows="3"
                  placeholder="Brief description of this category"
              ></textarea>
            </div>
            <div class="mb-3">
              <label for="colorCode" class="form-label">Color</label>
              <div class="d-flex">
                <input
                    type="color"
                    class="form-control form-control-color me-2"
                    id="colorCode"
                    v-model="categoryForm.colorCode"
                    title="Choose a color for the category"
                >
                <div class="color-preview flex-grow-1 rounded border" :style="{ backgroundColor: categoryForm.colorCode }">
                  <div class="color-preview-text text-white text-center py-1">{{ categoryForm.categoryName || 'Category Preview' }}</div>
                </div>
              </div>
              <div class="mt-3">
                <label class="form-label d-block">Quick Colors</label>
                <div class="d-flex gap-2">
                  <button @click.prevent="setColor('#e74c3c')" type="button" class="color-btn" style="background-color: #e74c3c;"></button>
                  <button @click.prevent="setColor('#9b59b6')" type="button" class="color-btn" style="background-color: #9b59b6;"></button>
                  <button @click.prevent="setColor('#2ecc71')" type="button" class="color-btn" style="background-color: #2ecc71;"></button>
                  <button @click.prevent="setColor('#3498db')" type="button" class="color-btn" style="background-color: #3498db;"></button>
                  <button @click.prevent="setColor('#f39c12')" type="button" class="color-btn" style="background-color: #f39c12;"></button>
                  <button @click.prevent="setColor('#1abc9c')" type="button" class="color-btn" style="background-color: #1abc9c;"></button>
                  <button @click.prevent="setColor('#34495e')" type="button" class="color-btn" style="background-color: #34495e;"></button>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="submit" form="categoryForm" class="btn btn-primary" :disabled="submitLoading">
            <span v-if="submitLoading" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            {{ isEditMode ? 'Update Category' : 'Create Category' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import HrPageLayout from "./HrPageLayout.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "HolidayCategories",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      submitLoading: false,
      categories: [],
      categoryUsage: [],
      selectedCategory: null,
      isEditMode: false,
      categoryForm: {
        categoryName: "",
        description: "",
        colorCode: "#3498db"
      }
    };
  },
  mounted() {
    this.getCategories();
  },
  methods: {
    getCategories() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getHolidayCategories");

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.categories = res.data.data.categories || [];
              this.getHolidayStatistics();
            } else {
              console.error("Error fetching categories:", res.data.message);
              this.showToast("error", "Failed to load categories: " + res.data.message);
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching categories");
          });
    },

    getHolidayStatistics() {
      const data = new FormData();
      data.append("function", "getHolidayStatistics");
      data.append("year", new Date().getFullYear());

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success && res.data.data.categoryStats) {
              // Calculate percentages for display
              const stats = res.data.data.categoryStats;
              const totalHolidays = stats.reduce((total, cat) => total + cat.count, 0);

              this.categoryUsage = stats.map(cat => ({
                id: cat.id,
                name: cat.category_name,
                count: cat.count,
                color: cat.color_code,
                percentage: totalHolidays > 0 ? Math.round((cat.count / totalHolidays) * 100) : 0
              }));

              // Sort by count in descending order
              this.categoryUsage.sort((a, b) => b.count - a.count);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    createCategory() {
      this.isEditMode = false;
      this.categoryForm = {
        categoryName: "",
        description: "",
        colorCode: "#3498db"
      };

      new bootstrap.Modal(document.getElementById('categoryModal')).show();
    },

    editCategory(category) {
      this.isEditMode = true;
      this.selectedCategory = category;

      this.categoryForm = {
        categoryId: category.id,
        categoryName: category.category_name,
        description: category.description || "",
        colorCode: category.color_code || "#3498db"
      };

      new bootstrap.Modal(document.getElementById('categoryModal')).show();
    },

    submitCategory() {
      if (!this.categoryForm.categoryName.trim()) {
        this.showToast("error", "Category name is required");
        return;
      }

      this.submitLoading = true;
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateHolidayCategory");
        data.append("categoryId", this.categoryForm.categoryId);
      } else {
        data.append("function", "createHolidayCategory");
      }

      data.append("categoryName", this.categoryForm.categoryName);
      data.append("description", this.categoryForm.description);
      data.append("colorCode", this.categoryForm.colorCode);

      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;

            if (res.data.success) {
              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('categoryModal'));
              if (modal) {
                modal.hide();
              }

              this.showToast("success", res.data.message);
              this.getCategories(); // Refresh the data
            } else {
              this.showToast("error", res.data.message || "Failed to save category");
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when saving category");
          });
    },

    deleteCategory(category) {
      // Check if category is in use
      if (this.isCategoryInUse(category.id)) {
        this.showToast("error", "Cannot delete a category that is in use by holidays");
        return;
      }

      if (confirm(`Are you sure you want to delete the category "${category.category_name}"?`)) {
        const data = new FormData();
        data.append("function", "deleteHolidayCategory");
        data.append("categoryId", category.id);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                this.showToast("success", res.data.message);
                this.getCategories(); // Refresh the data
              } else {
                this.showToast("error", res.data.message);
              }
            })
            .catch((error) => {
              console.error("API error:", error);
              this.showToast("error", "Network error when deleting category");
            });
      }
    },

    getCategoryUsage(categoryId) {
      const categoryStats = this.categoryUsage.find(cat => cat.id === categoryId);
      return categoryStats ? categoryStats.count : 0;
    },

    isCategoryInUse(categoryId) {
      return this.getCategoryUsage(categoryId) > 0;
    },

    setColor(color) {
      this.categoryForm.colorCode = color;
    },

    truncateText(text, maxLength) {
      if (!text) return '';

      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    }
  }
};
</script>

<style scoped>
.category-color-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-block;
}

.color-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: transform 0.2s;
}

.color-btn:hover {
  transform: scale(1.1);
}

.color-preview {
  min-height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-preview-text {
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.category-bar {
  transition: transform 0.3s;
}

.category-bar:hover {
  transform: translateX(5px);
}
</style>